import { z } from 'zod';

import { auditFieldsSchema, entityIdSchema, isoDateTimeSchema, pageMetaSchema } from './common.js';

export const roleSchema = z.enum([
  'admin',
  'direction',
  'reservation',
  'compta',
  'guide',
  'lecture',
]);

export const permissionSchema = z.enum([
  'customers:read',
  'customers:write',
  'customer-labels:write',
  'customers:import',
  'reservations:read',
  'reservations:write',
  'documents:manage',
  'gift-voucher-documents:manage',
  'planning:write',
  'finance:read',
  'finance:write',
  'operations:read',
  'operations:write',
  'resources:catalog:read',
  'resources:catalog:write',
  'resources:assignable:read',
  'personnel:contacts:read',
  'assignments:read',
  'assignments:write',
  'stock:read',
  'stock:write',
  'epi:read',
  'epi:write',
  'epi:control',
  'logistics:supply:read',
  'logistics:supply:write',
  'logistics:supply:settle',
  'logistics:cold:read',
  'logistics:cold:record',
  'logistics:cold:configure',
  'administration:read',
  'administration:write',
  'users:read',
  'users:write',
  'profile:read',
  'profile:write',
]);

export const passwordSchema = z
  .string()
  .min(12, 'Le mot de passe doit contenir au moins 12 caractères.')
  .max(256, 'Le mot de passe est trop long.')
  .regex(/[a-zà-öø-ÿ]/u, 'Ajoutez une lettre minuscule.')
  .regex(/[A-ZÀ-ÖØ-Þ]/u, 'Ajoutez une lettre majuscule.')
  .regex(/\d/u, 'Ajoutez un chiffre.')
  .regex(/[^\p{L}\p{N}\s]/u, 'Ajoutez un caractère spécial.');

const userIdentitySchema = z.object({
  id: entityIdSchema,
  email: z.email(),
  displayName: z.string().trim().min(1).max(160),
  role: roleSchema,
  active: z.boolean(),
  archived: z.boolean(),
});

export const userSchema = userIdentitySchema.extend(auditFieldsSchema.shape);

export const userCreateInputSchema = z.object({
  email: z.email(),
  displayName: z.string().trim().min(1).max(160),
  role: roleSchema,
  active: z.boolean().default(true),
  password: passwordSchema,
});

export const userUpdateInputSchema = z.object({
  displayName: z.string().trim().min(1).max(160).optional(),
  role: roleSchema.optional(),
  active: z.boolean().optional(),
  revision: z.number().int().positive(),
});

export const userArchiveInputSchema = z.object({
  revision: z.number().int().positive(),
});

export const userPageSchema = z.object({ items: z.array(userSchema), page: pageMetaSchema });

export const sessionUserSchema = userIdentitySchema.pick({
  id: true,
  email: true,
  displayName: true,
  role: true,
  active: true,
  archived: true,
});

export const loginInputSchema = z.object({
  email: z.email(),
  password: z.string().min(12).max(256),
});

export const setupInputSchema = loginInputSchema.extend({
  displayName: z.string().trim().min(1).max(160),
  password: passwordSchema,
});

export const forgotPasswordInputSchema = z.object({ email: z.email() });

export const resetPasswordInputSchema = z.object({
  token: z.string().min(32).max(512),
  email: z.email(),
  password: passwordSchema,
});

export const setupStatusSchema = z.object({ available: z.boolean() });

export const userArchiveResultSchema = z.object({ archived: z.literal(true) });

export const userPreferencesSchema = z.object({
  theme: z.enum(['system', 'light', 'dark']),
  density: z.enum(['comfortable', 'compact']),
  revision: z.number().int().positive(),
});

export const profileSchema = z.object({
  user: sessionUserSchema,
  preferences: userPreferencesSchema,
  revision: z.number().int().positive(),
});

export const profileUpdateInputSchema = z.object({
  displayName: z.string().trim().min(1).max(160),
  theme: userPreferencesSchema.shape.theme,
  density: userPreferencesSchema.shape.density,
  revision: z.number().int().positive(),
  preferencesRevision: z.number().int().positive(),
});

export const passwordChangeInputSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: passwordSchema,
});

export const passwordChangedSchema = z.object({ changed: z.literal(true) });

export const passwordResetPreviewSchema = z.object({
  email: z.email(),
  token: z.string().min(32),
});

export const passwordResetAcceptedSchema = z.object({ accepted: z.literal(true) });
export const passwordResetCompletedSchema = z.object({ reset: z.literal(true) });

export const sessionSchema = z.object({
  user: sessionUserSchema,
  permissions: z.array(permissionSchema),
  expiresAt: isoDateTimeSchema,
});

export type Role = z.infer<typeof roleSchema>;
export type Permission = z.infer<typeof permissionSchema>;
export type SessionUser = z.infer<typeof sessionUserSchema>;
export type User = z.infer<typeof userSchema>;
export type UserCreateInput = z.infer<typeof userCreateInputSchema>;
export type UserUpdateInput = z.infer<typeof userUpdateInputSchema>;
export type UserPreferences = z.infer<typeof userPreferencesSchema>;
